import { Alert, Box, Button, Collapse, Stack, Tooltip, Typography } from '@mui/material'
import { useNavigate } from 'react-router-dom'
import { useMemo, useState } from 'react'
import { ColumnDef, ColumnSizingState, getCoreRowModel, useReactTable } from '@tanstack/react-table'
import { com } from '@eidu/web'
import useEntityTypes from '../../io/useEntityTypes'
import { usePermissionPoliciesInfinite } from '../../io/usePermissionPolicies'
import { PermissionPolicyDTO } from '../../domain/permission/PermissionPolicyDTO'
import computeActionsBreakdown, { ActionsBreakdown } from '../../domain/permission/computeActionsBreakdown'
import { StringListCell, TypesPermissionsCell } from './permissionListCells'
import InfiniteScrollTable from '../../components/data/InfiniteScrollTable'
import AccessControlTabs from './AccessControlTabs'
import useHasGlobalPermissions from './useHasGlobalPermissions'
import EntityType = com.eidu.sharedlib.entity.type.EntityType
import EntityTypeId = com.eidu.sharedlib.entity.type.EntityTypeId
import GlobalAction = com.eidu.sharedlib.entity.permission.GlobalAction

const NameCell = ({ value }: { value: string }) => (
  <Tooltip title={value} placement="top-start">
    <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', overflow: 'hidden' }}>
      <Typography variant="body2" noWrap sx={{ lineHeight: 1.3, fontWeight: 600 }}>
        {value}
      </Typography>
    </Box>
  </Tooltip>
)

type PolicyRow = {
  id: string
  rowNumber: number
  name: string
  actions: ActionsBreakdown
}

type PermissionPoliciesListPageContentProps = {
  policies: ReadonlyArray<PermissionPolicyDTO>
  allEntityTypes: ReadonlyMap<EntityTypeId, EntityType | null> | undefined
  totalCount: number
  hasMore: boolean
  loading: boolean
  loadMore: () => void
  onNewClicked: () => void
  onPolicyClicked: (id: string) => void
}

const PermissionPoliciesListPageContent = ({
  policies,
  allEntityTypes,
  totalCount,
  hasMore,
  loading,
  loadMore,
  onNewClicked,
  onPolicyClicked,
}: PermissionPoliciesListPageContentProps) => {
  const rows: PolicyRow[] = useMemo(
    () =>
      policies.map((policyDocument, index) => ({
        id: policyDocument.policyId.toString(),
        rowNumber: index + 1,
        name: policyDocument.policy.name,
        actions: computeActionsBreakdown(
          [...policyDocument.policy.permissions.asJsReadonlySetView()],
          allEntityTypes ?? new Map()
        ),
      })),
    [policies, allEntityTypes]
  )

  const columns = useMemo<ColumnDef<PolicyRow>[]>(
    () => [
      {
        id: 'row',
        header: () => <span>Row</span>,
        accessorKey: 'rowNumber',
        size: 70,
        minSize: 50,
        enableResizing: false,
      },
      {
        id: 'name',
        header: () => <span>Name</span>,
        cell: ({ row }) => <NameCell value={row.original.name} />,
        size: 300,
        minSize: 150,
      },
      {
        id: 'globalPermissions',
        header: () => <span>Global permissions</span>,
        cell: ({ row }) => <StringListCell value={row.original.actions.global} />,
        size: 300,
        minSize: 150,
      },
      {
        id: 'typesPermissions',
        header: () => <span>Types permissions</span>,
        cell: ({ row }) => <TypesPermissionsCell value={row.original.actions.perType} />,
        size: 500,
        minSize: 200,
      },
    ],
    []
  )

  const [columnSizing, setColumnSizing] = useState<ColumnSizingState>({})

  const table = useReactTable({
    data: rows,
    columns,
    state: { columnSizing },
    onColumnSizingChange: setColumnSizing,
    columnResizeMode: 'onChange',
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => row.id,
  })

  return (
    <Stack
      sx={{
        flex: 1,
        minHeight: 0,
        p: 3,
        overflow: 'hidden',
      }}
      spacing={2}
    >
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          border: 1,
          borderColor: 'divider',
          borderRadius: 1,
          overflow: 'hidden',
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{
            px: 2,
            py: 1,
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
            borderBottom: 1,
            borderColor: 'divider',
            flexShrink: 0,
          }}
        >
          <Stack>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Permission policies
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.85 }}>
              {totalCount} total
            </Typography>
          </Stack>
          <Stack direction="row" spacing={1}>
            <Button color="inherit" variant="outlined" size="small" onClick={onNewClicked}>
              New policy
            </Button>
          </Stack>
        </Stack>
        <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          <InfiniteScrollTable
            table={table}
            loading={loading}
            hasMore={hasMore}
            loadMore={loadMore}
            emptyMessage="No permission policies"
            containerTestId="permission-policies-table"
            onRowClick={(row) => onPolicyClicked(row.id)}
            estimateRowSize={56}
          />
        </Box>
      </Box>
    </Stack>
  )
}

const PermissionPoliciesListPage = () => {
  const navigate = useNavigate()
  const { entityTypes: allEntityTypes, error: typesError } = useEntityTypes()
  const { policies, totalCount, hasMore, loading, loadMore, error: policiesError } = usePermissionPoliciesInfinite()

  const { hasPermission, loading: permissionsLoading } = useHasGlobalPermissions(GlobalAction.ManagePermissionGrants)
  const hasManagePermissionGrantsPermission = hasPermission(GlobalAction.ManagePermissionGrants)
  const error = typesError ?? policiesError

  if (permissionsLoading || hasManagePermissionGrantsPermission === false) return null

  return (
    <Stack sx={{ flex: 1, minHeight: 0 }}>
      <AccessControlTabs />
      <Collapse in={!!error}>
        <Alert
          variant="filled"
          severity="error"
          sx={{ margin: 2 }}
          action={
            <Button color="inherit" size="small" onClick={() => window.location.reload()}>
              Reload
            </Button>
          }
        >
          <Box>{String(error)}</Box>
          <Box>Please reload the page and try again.</Box>
        </Alert>
      </Collapse>
      {!error && (
        <PermissionPoliciesListPageContent
          policies={policies}
          allEntityTypes={allEntityTypes}
          totalCount={totalCount}
          hasMore={hasMore}
          loading={loading}
          loadMore={loadMore}
          onNewClicked={() => navigate('new')}
          onPolicyClicked={(id) => navigate(id)}
        />
      )}
    </Stack>
  )
}

const PermissionPoliciesListPageRoute = () => <PermissionPoliciesListPage />

export { PermissionPoliciesListPage }
export default PermissionPoliciesListPageRoute
